import { useCallback } from "react";

import { FormInstance } from "antd/es/form/hooks/useForm";
import { IntlShape } from "react-intl";

import CustomField, {
  CustomFieldPayload,
} from "@mapmycustomers/shared/types/customField/CustomField";
import { EntityTypeSupportingCustomFields } from "@mapmycustomers/shared/types/entity";
import Role from "@mapmycustomers/shared/types/Role";
import Team from "@mapmycustomers/shared/types/Team";

import DefaultCustomFieldFormValues from "@app/scene/settings/component/CustomFields/type/DefaultCustomFieldFormValues";
import isDataTypeOptionable from "@app/scene/settings/component/CustomFields/util/isDataTypeOptionable";
import { createField as createFieldAction } from "@app/scene/settings/component/ManageFields/store/actions";
import showSaveSettingSuccessfulMessage from "@app/scene/settings/util/showSaveSettingSuccessfulMessage";
import { updateCustomField as updateCustomFieldAction } from "@app/store/customFields/actions";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";
import { createTeamVisibilityFromRoleVisibility } from "@app/util/schema/fieldVisibilityRecord";

const useHandleFinish = (
  createCustomField: typeof createFieldAction.request,
  customField: CustomField | CustomFieldPayload,
  customFields: CustomField[],
  entityType: EntityTypeSupportingCustomFields,
  intl: IntlShape,
  form: FormInstance<DefaultCustomFieldFormValues>,
  onHide: () => void,
  updateCustomField: typeof updateCustomFieldAction.request,
  roles: Role[],
  teams: Team[],
  analyticIssuer?: AnalyticsService
) =>
  useCallback(() => {
    analyticIssuer?.completed(["Share & Save", "Save"]);

    const formValues = form.getFieldsValue();

    const {
      currencyId,
      dataType,
      displayName,
      displayType,
      isUnique,
      options,
      phiEnabled,
      placeholder,
      required,
      teamId,
      visibility,
    } = formValues;

    const teamVisibility = formValues.unifiedVisibility
      ? createTeamVisibilityFromRoleVisibility(formValues.roleVisibility, roles, teams)
      : formValues.teamVisibility;

    const completedCallback = () => {
      showSaveSettingSuccessfulMessage(intl);
      onHide();
    };

    if (customField?.id) {
      updateCustomField({
        callback: completedCallback,
        customField: {
          ...(customField as CustomField),
          currencyId,
          dataType,
          displayName,
          displayType,
          isUnique,
          options: options ?? [],
          placeholder,
          teamId,
        },
        entityType,
      });
    } else {
      const customField = {
        crmPropertyKey: displayName.toLowerCase().replace(/ /g, "_"),
        currencyId,
        dataType,
        defaultValue: [],
        displayName,
        displayOrder: customFields.length + 1,
        displayType,
        isUnique,
        options: isDataTypeOptionable(dataType) ? options : [],
        placeholder,
        required: required ?? false,
        teamId,
      };

      const field = {
        phiEnabled,
        teamVisibility,
        visibility,
      };

      createCustomField({
        callback: completedCallback,
        customField,
        entityType,
        field,
      });
    }
  }, [
    analyticIssuer,
    createCustomField,
    customField,
    customFields,
    entityType,
    intl,
    form,
    onHide,
    roles,
    teams,
    updateCustomField,
  ]);

export default useHandleFinish;
